export const formatDate = (
  date: Date | string | number,
  t?: (key: string) => string
): string => {
  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) {
      return t ? t('common.invalidDate') : 'Invalid Date';
    }
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return t ? t('common.invalidDate') : 'Invalid Date';
  }
};

export const formatDateTime = (
  date: Date | string | number,
  t?: (key: string) => string
): string => {
  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) {
      return t ? t('common.invalidDate') : 'Invalid Date';
    }
    return dateObj.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return t ? t('common.invalidDate') : 'Invalid Date';
  }
};

export const formatCurrency = (
  amount: number,
  currency: string = 'USD'
): string => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
};

export const formatNumber = (num: number): string => {
  try {
    return new Intl.NumberFormat('en-US').format(num);
  } catch {
    return String(num);
  }
};

/**
 * Format numbers with K/M notation for compact display
 * Examples: 1500 -> "1.5K", 2500000 -> "2.5M"
 */
export const formatNumberCompact = (num: number): string => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  } else if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toString();
};

export const formatFileSize = (
  bytes: number,
  t?: (key: string) => string
): string => {
  const sizes = t
    ? [
        t('utils.bytes'),
        t('utils.kb'),
        t('utils.mb'),
        t('utils.gb'),
        t('utils.tb'),
      ]
    : ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return t ? `0 ${t('utils.bytes')}` : '0 Bytes';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
};

export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

export const capitalize = (str: string, options: any = {}) => {
  const {
    mode = 'first', // 'first', 'words', 'sentences', 'proper'
    preserveCase = false,
    separators = [' ', '-', '_', '.'],
    excludeWords = [], // Words to keep lowercase (articles, prepositions)
  } = options;

  if (!str?.length) return str;

  const baseStr = preserveCase ? str : str.toLowerCase();

  switch (mode) {
    case 'first':
      return baseStr.charAt(0).toUpperCase() + baseStr.slice(1);

    case 'words':
      return baseStr.replace(/\b\w/g, (letter, index, string) => {
        const wordStart = string.lastIndexOf(' ', index) + 1;
        const wordEnd = string.indexOf(' ', index);
        const word = string.slice(
          wordStart,
          wordEnd === -1 ? undefined : wordEnd
        );

        if (excludeWords.includes(word.toLowerCase()) && index !== 0) {
          return letter;
        }
        return letter.toUpperCase();
      });

    case 'sentences':
      return baseStr.replace(/(^\w|[.!?]\s*\w)/g, letter =>
        letter.toUpperCase()
      );

    case 'proper':
      let result = baseStr.charAt(0).toUpperCase() + baseStr.slice(1);
      const pattern = new RegExp(
        `([${separators.map((s: string) => '\\' + s).join('')}])(\\w)`,
        'g'
      );
      return result.replace(
        pattern,
        (_, __, letter) => ' ' + letter.toUpperCase()
      );

    default:
      return baseStr;
  }
};

export const camelToTitle = (str: string): string => {
  return str
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

export const formatDateString = (d: string) => {
  const [datePart, timePart = '00:00:00'] = d.trim().split(' ');
  const [dd, mm, yyyy] = datePart.split('-').map(Number);
  const [hh, mi, ss] = timePart.split(':').map(Number);

  // Create date safely using numbers (NOT string parsing)
  const date = new Date(yyyy, mm - 1, dd, hh, mi, ss);

  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const sanitizeFileName = (fileName: string): string => {
  const KNOWN_EXT = new Set([
    'jpg',
    'jpeg',
    'png',
    'gif',
    'webp',
    'mp4',
    'mov',
    'avi',
    'pdf',
  ]);
  const parts = fileName.split('.');
  const ext =
    parts.length > 1 && KNOWN_EXT.has(parts[parts.length - 1].toLowerCase())
      ? parts.pop()!.toLowerCase()
      : '';
  const cleanName = parts
    .filter(part => !KNOWN_EXT.has(part.toLowerCase()))
    .join('.');
  const sanitized = cleanName.replace(/[^a-zA-Z0-9_-]/g, '_');
  return `${sanitized}${ext ? `.${ext}` : ''}`;
};
